import React from 'react';
import OttoAlive from './OttoAlive';
import useOttoPerch from './useOttoPerch';
import './otto.css';

// Otto's permanent home on the admin dashboard — a dark "briefing bar"
// (matches the sidebar surface #101113) where the interactive everyday
// Otto delivers the day's priority in his own voice. It reads from the
// dashboard's recap ladder so it never duplicates a fetch, and it's the
// stage for the click micro-animations and the once-a-session ejector
// treat. Admin/internal only.
//
// State ladder (highest priority first):
//   1. intro     — first login of the session; the "Hello, I'm Otto"
//                  welcome, dismissible with × (returns next login).
//   2. allClear  — nothing awaiting responses; a calm sign-off.
//   3. briefing  — the recap headline (urgent framing carried through).
const DOT_HEX = { red: '#E0290A', amber: '#E89A2E', grey: '#9BA08C', teal: '#2E8FA1', green: '#92D400' };

export default function OttoBriefingBar({
  firstName,
  recap,
  showIntro = false,
  onDismissIntro,
  onNavigateBriefs,
  // Attention lines — cross-cutting things waiting on the user (questions,
  // update requests, overdue), each { dot, title, body, ctaLabel, onCta }.
  // Rendered as severity-dotted lines beneath the briefing.
  attention = [],
}) {
  const name = firstName || 'there';
  // He is one owl: while the roaming overlay has him, this perch is empty.
  const perch = useOttoPerch();

  let heading;
  let body;
  if (showIntro) {
    heading = "Hello, I'm Otto — your Pilot Owl";
    body = (
      <>I'll keep an eye on your briefs and help you get the suppliers on board.
      You'll see me pop up around Supply Pilot.</>
    );
  } else if (recap?.allClear) {
    heading = `All clear, ${name}`;
    body = <>Nothing's awaiting a response right now — you're all caught up. I'll keep watch.</>;
  } else {
    heading = `Here's your briefing, ${name}`;
    body = (
      <>
        <button
          type="button"
          onClick={onNavigateBriefs}
          className="font-bold text-white hover:text-fusion-green transition-colors bg-transparent border-0 p-0 cursor-pointer font-['Metropolis']">
          {recap?.leadCount} {recap?.leadWord}
        </button>{' '}
        {recap?.leadTail}
        {recap?.urgent && <>, <span className="text-fusion-green-light font-semibold">{recap.urgent}</span></>}
      </>
    );
  }

  return (
    // overflow-visible so the ejector-seat treat can lift Otto above the bar.
    // Forest-green "cockpit" (build brief #8) — reads as a deliberate panel
    // and stops competing with the near-black sidebar.
    <div className="relative overflow-visible bg-fusion-forest border border-white/[0.08] rounded-card shadow-card p-6 mb-8">
      {/* Top row — Otto sits centred against the greeting (heading + body),
          not against the whole stack, so his face lines up with the text
          beside him regardless of how many attention lines follow. */}
      <div className="flex gap-5 items-center">
        {/* His perch. The BOX keeps its size whether he's here or not — a
            briefing bar that reflowed every time he went for a fly would be
            far more distracting than the two Ottos this fixes. */}
        <div className="flex-shrink-0" style={{ width: 88, height: 88 }}>
          {/* Everyday Otto, ejectable (his home is the only place the rare
              ejector-seat treat fires). Captain is reserved for the airshow
              celebration, which portals in over the page.
              Hidden outright while the roamer has him, so there is only ever
              one Otto on the page. */}
          {!perch.hidden && (
            <div className={perch.className}>
              <OttoAlive size={88} ejectable />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-['Metropolis'] text-[15px] font-extrabold text-white">
            {heading}
          </h3>
          <p className="font-['Metropolis'] text-[13px] text-sb-text leading-relaxed mt-1.5 max-w-[62ch]">
            {body}
          </p>
        </div>
      </div>
      {/* Attention lines — what's waiting on you, ordered/coloured by
          severity. Full-width beneath both columns so they get the whole
          card and don't shove Otto to the top. */}
      {attention.length > 0 && (
        <div className="mt-4 pt-3.5 border-t border-white/10 space-y-2">
          {attention.map((a, i) => (
            <div key={i} className="flex items-baseline gap-2 flex-wrap">
              {a.dot && <span aria-hidden="true" className="w-2 h-2 rounded-full flex-shrink-0 self-center" style={{ background: DOT_HEX[a.dot] }} />}
              <span className="font-['Metropolis'] text-[13.5px] font-bold text-white">{a.title}</span>
              {a.body && <span className="font-['Metropolis'] text-[12px] text-sb-text">{a.body}</span>}
              {a.ctaLabel && (
                <button type="button" onClick={a.onCta}
                  className="font-['Metropolis'] text-[12px] font-bold text-fusion-green-light hover:text-white underline underline-offset-2 bg-transparent border-0 cursor-pointer">
                  {a.ctaLabel}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {showIntro && (
        <button
          type="button"
          onClick={onDismissIntro}
          aria-label="Dismiss"
          className="absolute right-3 top-3 text-sb-muted hover:text-white text-lg leading-none bg-transparent border-0 cursor-pointer">
          ×
        </button>
      )}
    </div>
  );
}
